import { useRef, useEffect, useState } from 'react'
import { RiArrowLeftSLine, RiArrowRightSLine, RiCloseCircleLine } from 'react-icons/ri'
import styles from './PageSelector.module.css'

interface Page {
    id: number
    page_number: number
}

interface PageSelectorProps {
    label: string
    pages: Page[]
    selectedId: number | null
    onSelect: (id: number | null) => void
    allowNone?: boolean
    noneLabel?: string
}

export function PageSelector({ label, pages, selectedId, onSelect, allowNone, noneLabel = 'Ninguna' }: PageSelectorProps) {
    const scrollRef = useRef<HTMLDivElement>(null)
    const [jumpInput, setJumpInput] = useState('')
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    const checkScroll = () => {
        const el = scrollRef.current
        if (!el) return
        setCanScrollLeft(el.scrollLeft > 2)
        setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2)
    }

    useEffect(() => {
        checkScroll()
        const el = scrollRef.current
        if (!el) return
        el.addEventListener('scroll', checkScroll, { passive: true })
        const ro = new ResizeObserver(checkScroll)
        ro.observe(el)
        return () => {
            el.removeEventListener('scroll', checkScroll)
            ro.disconnect()
        }
    }, [pages])

    // Auto-scroll selected into view
    useEffect(() => {
        if (!scrollRef.current || selectedId === null) return
        const card = scrollRef.current.querySelector(`[data-page-id="${selectedId}"]`) as HTMLElement
        if (card) {
            card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
        }
    }, [selectedId])

    const scroll = (direction: 'left' | 'right') => {
        const el = scrollRef.current
        if (!el) return
        const amount = el.clientWidth * 0.6
        el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' })
    }

    const handleJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter') return
        const num = parseInt(jumpInput, 10)
        if (isNaN(num)) return
        const page = pages.find((p) => p.page_number === num)
        if (page) {
            onSelect(page.id)
            setJumpInput('')
        }
    }

    const showArrows = pages.length > 5

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <span className={styles.label}>{label}</span>
                {pages.length > 10 && (
                    <div className={styles.jumpWrapper}>
                        <input
                            type="number"
                            className={styles.jumpInput}
                            placeholder="Ir a pág..."
                            min={1}
                            max={pages.length}
                            value={jumpInput}
                            onChange={(e) => setJumpInput(e.target.value)}
                            onKeyDown={handleJump}
                        />
                    </div>
                )}
            </div>

            <div className={styles.scrollArea}>
                {showArrows && canScrollLeft && (
                    <button className={`${styles.scrollBtn} ${styles.scrollLeft}`} onClick={() => scroll('left')} type="button">
                        <RiArrowLeftSLine />
                    </button>
                )}

                <div className={styles.track} ref={scrollRef}>
                    {allowNone && (
                        <button
                            className={`${styles.card} ${selectedId === null ? styles.cardSelected : ''}`}
                            onClick={() => onSelect(null)}
                            type="button"
                        >
                            <div className={styles.thumb}>
                                <RiCloseCircleLine className={styles.thumbNone} />
                            </div>
                            <span className={styles.cardLabel}>{noneLabel}</span>
                        </button>
                    )}
                    {pages.map((p) => (
                        <button
                            key={p.id}
                            data-page-id={p.id}
                            className={`${styles.card} ${selectedId === p.id ? styles.cardSelected : ''}`}
                            onClick={() => onSelect(p.id)}
                            type="button"
                        >
                            <div className={styles.thumb}>
                                <span>{p.page_number}</span>
                            </div>
                            <span className={styles.cardLabel}>Pág. {p.page_number}</span>
                        </button>
                    ))}
                </div>

                {showArrows && canScrollRight && (
                    <button className={`${styles.scrollBtn} ${styles.scrollRight}`} onClick={() => scroll('right')} type="button">
                        <RiArrowRightSLine />
                    </button>
                )}
            </div>

            {selectedId !== null && (
                <span className={styles.selectedInfo}>
                    Página {pages.find((p) => p.id === selectedId)?.page_number} seleccionada
                </span>
            )}
        </div>
    )
}
